import React from "react";
import { Box, Typography, Paper, Button, Stack, Avatar } from "@mui/material";
import { OpenInNew, ThumbUpOutlined } from "@mui/icons-material";
import { useTranslatedDictionary } from "./hooks/useTranslatedDictionary";

const AD_TEXT = {
  es: {
    sponsored: "📢 ANUNCIO PATROCINADO",
    visitWebsite: "Visita nuestro sitio web para más información",
    discoverProfile: "Descubre más sobre este perfil",
    impressions: "impresiones",
    expiresIn: "Expira en {n} días",
    visit: "Visitar",
    viewProfile: "Ver Perfil",
    viewPost: "Ver Publicación",
  },
  en: {
    sponsored: "📢 SPONSORED AD",
    visitWebsite: "Visit our website for more information",
    discoverProfile: "Discover more about this profile",
    impressions: "impressions",
    expiresIn: "Expires in {n} days",
    visit: "Visit",
    viewProfile: "View Profile",
    viewPost: "View Post",
  },
};

const AnuncioCard = ({ ad, onClickAd, onOpenAdvertiserProfile, isLightTheme = false, language = "es" }) => {
  const t = useTranslatedDictionary(AD_TEXT, language);

  if (!ad) return null;

  const onSurface = (opacity = 1) =>
    isLightTheme ? `rgba(15,20,32,${opacity})` : `rgba(255,255,255,${opacity})`;

  const isVideoUrl = (url) => /\.(mp4|webm|ogg)(\?|$)/i.test(url || "");

  const getNormalizedAdMedia = () => {
    if (Array.isArray(ad.postMedia) && ad.postMedia.length > 0) {
      return ad.postMedia;
    }

    return [
      ...(ad.postImage ? [{ type: "image", url: ad.postImage }] : []),
      ...(ad.postVideo ? [{ type: "video", url: ad.postVideo }] : []),
    ];
  };

  const getAdContent = () => {
    const mediaItems = getNormalizedAdMedia();
    const videoItem =
      mediaItems.find((item) => item?.type === "video") ||
      mediaItems.find((item) => isVideoUrl(item?.url));
    const imageItem =
      mediaItems.find((item) => item?.type === "image") ||
      mediaItems.find((item) => item?.url && !isVideoUrl(item.url));

    if (ad.adType === "post" && videoItem?.url) {
      return (
        <Box
          component="video"
          src={videoItem.url}
          controls
          muted
          playsInline
          sx={{ width: "100%", borderRadius: "12px", mb: 1.5, maxHeight: 340, objectFit: "cover", bgcolor: "#000" }}
          onClick={(event) => event.stopPropagation()}
        />
      );
    }

    if (ad.adType === "post" && imageItem?.url) {
      return (
        <Box
          component="img"
          src={imageItem.url}
          sx={{ width: "100%", borderRadius: "12px", mb: 1.5 }}
        />
      );
    }
    return null;
  };

  const handleClick = () => {
    // Incrementar click count
    onClickAd?.(ad.id);

    // Redirigir según tipo
    if (ad.adType === "website" && ad.externalUrl) {
      // Solo http(s): evita que un anuncio ejecute "javascript:" u otros esquemas.
      let safeUrl = null;
      try {
        const parsed = new URL(ad.externalUrl);
        if (parsed.protocol === "https:" || parsed.protocol === "http:") safeUrl = parsed.href;
      } catch {}
      if (safeUrl) window.open(safeUrl, "_blank", "noopener,noreferrer");
      return;
    }

    if ((ad.adType === "post" || ad.adType === "profile") && ad.ownerId) {
      onOpenAdvertiserProfile?.(ad.ownerId);
    }
  };

  const handleCtaClick = (event) => {
    event.stopPropagation();
    handleClick();
  };

  return (
    <Box
      sx={{
        display: "flex",
        mb: 5,
        position: "relative",
        zIndex: 1,
      }}
    >
      {/* Avatar */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mr: 2,
        }}
      >
        <Avatar
          src={ad.ownerPhoto}
          sx={{ width: 45, height: 45, cursor: "pointer" }}
        />
      </Box>

      {/* Card */}
      <Paper
        sx={{
          flex: 1,
          bgcolor: "rgba(0, 212, 255, 0.08)",
          border: "2px solid rgba(0, 212, 255, 0.3)",
          p: 2,
          borderRadius: "20px",
          color: onSurface(1),
          backdropFilter: "blur(10px)",
          cursor: "pointer",
          transition: "all 0.3s",
          "&:hover": {
            bgcolor: "rgba(0, 212, 255, 0.15)",
            borderColor: "#00d4ff",
            boxShadow: "0 0 20px rgba(0, 212, 255, 0.2)",
          },
        }}
        onClick={handleClick}
      >
        {/* Header con badge "Anuncio" */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Typography
            variant="caption"
            sx={{
              bgcolor: "rgba(0, 212, 255, 0.3)",
              px: 1.2,
              py: 0.5,
              borderRadius: "20px",
              color: "#00d4ff",
              fontWeight: 700,
              fontSize: "0.7rem",
            }}
          >
            {t("sponsored")}
          </Typography>
        </Stack>

        {/* Nombre del usuario */}
        <Typography
          variant="subtitle2"
          noWrap
          sx={{
            fontWeight: 600,
            mb: 1,
          }}
        >
          {ad.ownerName}
        </Typography>

        {/* Imagen del contenido */}
        {getAdContent()}

        {/* Título y descripción */}
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, color: "#00d4ff" }}>
          {ad.title}
        </Typography>

        <Typography variant="body2" sx={{ mb: 2, opacity: 0.85, minHeight: "40px" }}>
          {ad.postText && ad.postText.substring(0, 100)}
          {ad.adType === "website" && t("visitWebsite")}
          {ad.adType === "profile" && t("discoverProfile")}
        </Typography>

        {/* Info del anuncio */}
        <Stack
          direction="row"
          spacing={1}
          sx={{ mb: 2, pb: 2, borderBottom: "1px solid rgba(0,212,255,0.2)" }}
        >
          <Typography variant="caption" sx={{ color: onSurface(0.6) }}>
            👁️ {ad.impressions || 0} {t("impressions")}
          </Typography>
          <Typography variant="caption" sx={{ color: onSurface(0.6) }}>
            •
          </Typography>
          <Typography variant="caption" sx={{ color: onSurface(0.6) }}>
            {t("expiresIn").replace(
              "{n}",
              Math.ceil((new Date(ad.expiresAt) - new Date()) / (1000 * 60 * 60 * 24))
            )}
          </Typography>
        </Stack>

        {/* CTA Button */}
        <Button
          fullWidth
          variant="contained"
          onClick={handleCtaClick}
          endIcon={
            ad.adType === "website" ? <OpenInNew fontSize="small" /> : <ThumbUpOutlined fontSize="small" />
          }
          sx={{
            bgcolor: "#00d4ff",
            color: "#001217",
            fontWeight: 700,
            textTransform: "none",
            borderRadius: "10px",
            "&:hover": {
              bgcolor: "#00b8d9",
            },
          }}
        >
          {ad.adType === "website" ? t("visit") : ad.adType === "profile" ? t("viewProfile") : t("viewPost")}
        </Button>
      </Paper>
    </Box>
  );
};

export default AnuncioCard;
